<script setup lang="ts">
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    /**
     * In-page conversation outline (ChatGPT-style): one tiny bar per user
     * message, the current one highlighted. Hovering (or focusing) the rail
     * opens a panel with the clickable message previews.
     */

    const props = defineProps<{
        /** One entry per user message, in conversation order */
        items: { id: string; preview: string }[]
        /** Id of the message currently at the reading line */
        activeId?: string
    }>()

    const emit = defineEmits<{
        select: [id: string]
    }>()

    const { t: $t } = useI18n({
        useScope: 'global',
    })

    // Tighter bars on long conversations so the rail never overflows
    const isDense = computed(() => props.items.length > 14)
</script>

<template>
    <nav
        class="pk-chatbot-outline"
        :class="{ 'pk-chatbot-outline--dense': isDense }"
        :aria-label="$t('label.conversationOutline')">
        <!-- Visual rail only: the interactive targets are in the panel -->
        <div class="pk-chatbot-outline__bars" aria-hidden="true">
            <span
                v-for="item in items"
                :key="item.id"
                class="pk-chatbot-outline__bar"
                :class="{
                    'pk-chatbot-outline__bar--active': item.id === activeId,
                }" />
        </div>
        <div class="pk-chatbot-outline__panel">
            <button
                v-for="item in items"
                :key="item.id"
                type="button"
                class="pk-chatbot-outline__item"
                :class="{
                    'pk-chatbot-outline__item--active': item.id === activeId,
                }"
                :aria-current="item.id === activeId ? 'true' : undefined"
                @click="emit('select', item.id)">
                {{ item.preview }}
            </button>
        </div>
    </nav>
</template>

<style lang="scss">
    .pk-chatbot-outline {
        position: relative;
        display: flex;
        align-items: center;

        // Generous hover/touch area around the tiny bars
        padding: var(--spacing-12);

        &__bars {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: var(--spacing-6);
            max-height: 60vh;
            overflow: hidden;
        }

        &--dense &__bars {
            gap: var(--spacing-2);
        }

        &__bar {
            width: var(--spacing-16);
            height: var(--spacing-2);
            border-radius: var(--rounded-full);
            background-color: var(--color-surface-5);
            transition: var(--transition-colors);

            &--active {
                background-color: var(--color-word-2);
            }
        }

        // Preview panel: revealed by hovering the rail or tabbing into it.
        // Anchored to grow upwards: hanging below would inflate the
        // scrollable overflow of the chat past its real content
        &__panel {
            position: absolute;
            bottom: 0;
            right: 100%;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
            min-width: 180px;
            max-width: 280px;
            max-height: min(50vh, 480px);
            overflow-y: auto;
            padding: var(--spacing-6);
            border: 1px solid var(--color-surface-3);
            border-radius: var(--rounded-lg);
            background-color: var(--color-surface);
            box-shadow: var(--shadow-md);
            opacity: 0;
            visibility: hidden;
            translate: var(--spacing-8) 0;
            transition:
                opacity 150ms var(--ease-out),
                translate 150ms var(--ease-out),
                visibility 150ms;

            @media (prefers-reduced-motion: reduce) {
                transition: none;
            }
        }

        &:hover &__panel,
        &:focus-within &__panel {
            opacity: 1;
            visibility: visible;
            translate: 0 0;
        }

        &__item {
            // Never shrink below the natural height: the scrolling panel
            // would otherwise squash the items instead of overflowing
            flex-shrink: 0;
            padding: var(--spacing-6) var(--spacing-10);
            border-radius: var(--rounded-md);
            font-size: var(--text-12);
            color: var(--color-word-2);
            text-align: start;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            cursor: pointer;
            transition: var(--transition-colors);

            &:hover {
                background-color: var(--color-surface-1);
            }

            &--active {
                background-color: var(--color-surface-2);
                color: var(--color-word-1);
                font-weight: var(--font-semibold);
            }
        }
    }
</style>
